<template>
  <div class="custom-surveys-reports-wrapper">
    <div class="body">
      <div class="content">
        <PageLayout>
          <template slot="header">
            <div>
              <div class="d-flex m-3 mx-lg-0">
                <Breadcrumb :list="breadcrumbList" />
                <ExportData
                  id="exportData"
                  class="ml-sm-2 mt-2 mt-sm-0"
                  :canExport="$can('use', 'report.export')"
                  :hasXlsx="true"
                  :hasCsv="false"
                  @export="handleExportClick"
                />
                <Dropdown
                  v-if="false"
                  class="ml-md-3"
                  :data="groupOptions"
                  :selectedId="selectedGroupId"
                  :showSearch="true"
                  @selectItem="groupSelected"
                />
                <GroupsTreeSelect class="ml-md-1" @selectItem="groupSelected" />
              </div>
              <ReportsNavbar
                :canSeeSettings="summary.isOwner || isAdmin"
                :hasFeedbacks="summary.hasFeedbacks"
              />
            </div>
          </template>
          <template slot="main">
            <div v-if="isLoading" class="loading">
              <loading />
              <p>{{ labels.loading }}</p>
            </div>
            <div v-else class="page-reports">
              <div
                v-if="
                  isEmptyState ||
                    (report.history &&
                      report.history.length === 0 &&
                      !hasOnlyFeedbacks)
                "
                class="empty-state-box"
              >
                <b-alert show variant="warning">
                  <i class="material-icons">info</i>
                  {{ labels.empty | parse() }}
                </b-alert>
              </div>
              <b-card :class="{ 'inactive-group-card': inactive }">
                <header slot="header">
                  <b-row>
                    <b-col :title="labels.overall" md="10">
                      {{ labels.overall }}
                      <span v-if="report.groupName">
                        - {{ report.groupName }}
                      </span>
                    </b-col>
                  </b-row>
                </header>
                <h5>{{ summary.title }}</h5>
                <p>
                  {{
                    summary.description
                      ? summary.description
                      : labels.withoutDescription
                  }}
                </p>
                <div v-if="!isEmptyState && report.score !== null">
                  <div class="flex over-score">
                    <div class="flex flex-center report-date">
                      <Overall :score="report.score" />
                      <small v-if="report.score !== null" class="mt-2">
                        {{
                          reportsResources.overall.reportDate
                            | parse({
                              DATE: $moment(report.date).format('DD/MM/YYYY'),
                            })
                        }}
                      </small>
                      <small
                        v-if="report.score !== null"
                        class="engagement-notice"
                      >
                        {{ labels.generationNotice }}
                      </small>
                    </div>
                    <HistoricChart :history="report.history" />
                  </div>
                </div>
              </b-card>

              <br />

              <b-card
                :class="[
                  'metrics-container',
                  { 'chart-layout': isChart },
                  { 'inactive-group-card': inactive },
                ]"
                v-if="!isEmptyState && !showDetails && categories.length > 0"
              >
                <header :title="categoriesTitle" slot="header">
                  <div class="d-flex">
                    {{ categoriesTitle }}
                    <div class="switch-metric-type" v-if="!hasOnlyFeedbacks">
                      <b-link
                        @click.prevent="switchMetricType('chart')"
                        v-show="metricType === 'list'"
                      >
                        <i class="mdi mdi-chart-bar" aria-hidden="true"></i>
                        {{ reportsResources.metricList.showChart }}
                      </b-link>
                      <b-link
                        @click.prevent="switchMetricType('list')"
                        v-show="metricType === 'chart'"
                      >
                        <i class="mdi mdi-menu" aria-hidden="true"></i>
                        {{ reportsResources.metricList.showList }}
                      </b-link>
                    </div>
                  </div>
                </header>
                <MetricList
                  :metricType="metricType"
                  :list="categories"
                  @showDetails="showDetails = true"
                />
              </b-card>

              <br
                v-if="!isEmptyState && !showDetails && categories.length > 0"
              />

              <b-card
                v-if="!isEmptyState && showDetails"
                :class="{ 'inactive-group-card': inactive }"
              >
                <header :title="labels.detail" slot="header">
                  <div class="d-flex">
                    {{ labels.detail }}
                    <div class="switch-metric-type">
                      <b-link @click.prevent="showDetails = false">
                        <i class="mdi mdi-clipboard-outline" aria-hidden="true">
                        </i>
                        {{ labels.shortVersion }}
                      </b-link>
                    </div>
                  </div>
                </header>
                <div
                  class="category-detail-wrapper"
                  v-for="(category, cIdx) in categories"
                  :key="`category-${cIdx}`"
                >
                  <MetricItemCategory :category="category" />
                  <hr v-if="cIdx + 1 < categories.length" />
                </div>
              </b-card>

              <br v-if="!isEmptyState && showDetails" />

              <b-card v-if="!isEmptyState && report.withoutCategory">
                <header :title="labels.customizeQuestions" slot="header">
                  <div class="d-flex">
                    {{ labels.customizeQuestions }}
                  </div>
                </header>
                <MetricItemCategory :category="report.withoutCategory" />
              </b-card>

              <br v-if="!isEmptyState && report.withoutCategory" />

              <div class="npscores-wrapper" v-if="!isEmptyState">
                <div
                  class="npscores-container"
                  v-for="(npscore, npscoreIndex) in npscores"
                  :key="`npscore-${npscoreIndex}`"
                >
                  <b-card>
                    <header slot="header">
                      <b-row>
                        <b-col :title="npscore.text" cols="12">
                          {{ npscoreIndex + 1 }}.
                          <small>{{ npscore.text }}</small>
                        </b-col>
                      </b-row>
                    </header>
                    <div v-if="npscore.history" class="nps-component pt-4">
                      <Enps :enps="npscore" :label="getLabelByScore(npscore)" />
                      <div class="history">
                        <EnpsChart :nps="npscore" />
                      </div>
                    </div>
                    <div v-else class="enps-empty">
                      <h3>{{ reportsResources.metric.enps.empty }}</h3>
                    </div>
                  </b-card>
                  <br />
                </div>
              </div>

              <b-card v-if="!isEmptyState && !hasOnlyFeedbacks">
                <MetricItemSubtitle />
              </b-card>
            </div>
            <UnauthorizedModal ref="modal-unauthorized" />
          </template>
        </PageLayout>
      </div>
      <ReportsSidebar v-if="false" />
    </div>
  </div>
</template>

<script>
import { isEmpty } from 'ramda'
import { getUser, isAdmin } from '@/service/user'
import { mapGetters } from 'vuex'
import Dropdown from '@/components/Shared/Form/Dropdown.vue'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import Enps from '@/components/Reports/Enps.vue'
import EnpsChart from '@/components/CustomSurveys/Reports/EnpsChart.vue'
import HistoricChart from '@/components/CustomSurveys/Reports/HistoricChart.vue'
import MetricList from '@/components/CustomSurveys/Reports/MetricList.vue'
import MetricItemSubtitle from '@/components/CustomSurveys/Reports/MetricItemSubtitle.vue'
import MetricItemCategory from '@/components/CustomSurveys/Reports/MetricItemCategory.vue'
import UnauthorizedModal from '@/components/CustomSurveys/Reports/UnauthorizedModal.vue'
import Overall from '@/components/CustomSurveys/Reports/Overall.vue'
import GroupsTreeSelect from '@/components/CustomSurveys/Reports/GroupsTreeSelect.vue'
import PageLayout from '@/components/Home/PageLayout.vue'
import ReportsNavbar from '@/components/Home/CustomSurveys/ReportsNavbar.vue'
import ReportsSidebar from '@/components/Home/CustomSurveys/ReportsSidebar.vue'
import Loading from '@/components/Shared/Loading.vue'
import ExportData from '@/components/Reports/ExportData.vue'

export default {
  components: {
    Dropdown,
    Breadcrumb,
    HistoricChart,
    Enps,
    EnpsChart,
    MetricList,
    MetricItemSubtitle,
    MetricItemCategory,
    UnauthorizedModal,
    Overall,
    GroupsTreeSelect,
    PageLayout,
    ReportsNavbar,
    ReportsSidebar,
    Loading,
    ExportData,
  },
  data() {
    return {
      metricType: 'list',
      inactive: false,
      showDetails: false,
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      selectedGroupId: 'customSurveyResults/selectedGroupId',
      groups: 'customSurveyResults/groups',
      npscores: 'customSurveyResults/npscores',
      report: 'customSurveyResults/report',
      summary: 'customSurveyResults/summary',
    }),

    categoriesTitle() {
      return this.hasOnlyFeedbacks
        ? this.labels.categoriesInformation
        : this.labels.categories
    },

    hasOnlyFeedbacks() {
      if (this.isEmptyState) {
        return false
      }

      const withoutCategory = this.report.withoutCategory || {}
      const withoutCategoryQuestions = withoutCategory.questions || []

      const categories = this.report.categories || []
      const categoriesQuestions = categories.reduce((c, i) => {
        return [...c, ...i.questions]
      }, [])

      const questions = [
        ...withoutCategoryQuestions,
        ...categoriesQuestions,
      ].filter(q => q.type !== 'feedback')

      return questions.length === 0
    },

    isEmptyState() {
      return isEmpty(this.report)
    },

    sectionTitles: function() {
      return this.reportsResources.titles
    },

    isChart: function() {
      return this.metricType === 'chart'
    },

    labels: function() {
      return this.resources.page.home.customSurveys.report
    },

    reportsResources: function() {
      let reportsResources = this.resources.page.reports.engagement

      const mapped = {
        enpsTitle: reportsResources.metric.enps.title,
        lnpsTitle: reportsResources.metric.lnps.title,
        filters: reportsResources.filters,
        emptyState: reportsResources.emptyState,
      }

      reportsResources = { ...reportsResources, ...mapped }

      return reportsResources
    },
    groupId: function() {
      return this.$route.params.groupId
    },
    customSurveyId: function() {
      return this.$route.params.id
    },
    user() {
      return getUser()
    },
    isAdmin() {
      return isAdmin()
    },
    breadcrumbList() {
      return [
        {
          label: this.resources.page.home.customSurveys.title,
          link: { name: 'custom-surveys' },
        },
      ]
    },
    categories() {
      return this.report.categories ? this.report.categories : []
    },
    groupOptions() {
      return {
        groups: [
          {
            _id: 'consolidate',
            name: this.resources.page.home.customSurveys.consolidate,
            isConsolidate: true,
          },
          ...this.groups,
        ],
      }
    },
  },
  async created() {
    const requests = []
    requests.push(
      this.$store.dispatch('customSurveyResults/loadSummary', {
        customSurveyId: this.customSurveyId,
      })
    )
    requests.push(
      this.$store.dispatch('customSurveyResults/loadGroups', {
        customSurveyId: this.customSurveyId,
      })
    )
    requests.push(
      this.$store.dispatch('customSurveyResults/loadNpscores', {
        customSurveyId: this.customSurveyId,
        groupId: this.selectedGroupId,
      })
    )
    requests.push(
      this.$store.dispatch('customSurveyResults/loadReports', {
        customSurveyId: this.customSurveyId,
        groupId: this.selectedGroupId,
      })
    )
    this.isLoading = true
    try {
      await Promise.all(requests)
    } catch (error) {
      const { status } = error.response
      if (status === 401) {
        this.$refs['modal-unauthorized'].show()
      }
    }
    this.isLoading = false
  },
  mounted() {
    this.$root.$emit('setPageTitle', {
      context: this,
      path: 'custom-survey/report',
    })
  },
  methods: {
    handleExportClick(type) {
      // eslint-disable-next-line no-undef
      const env = window.env || process.env

      const selectedGroupId = this.selectedGroupId
        ? this.selectedGroupId
        : 'consolidate'

      window.open(
        `${env.VUE_APP_EXPORTER_API_HOST}/custom-surveys/${this.customSurveyId}/reports/${selectedGroupId}/${type}?token=${localStorage.jwt}&language=${localStorage.languageabrev}`,
        '_blank'
      )
    },
    async groupSelected(group) {
      const groupId = group._id
      const customSurveyId = this.customSurveyId

      const requests = []
      requests.push(
        this.$store.dispatch('customSurveyResults/loadNpscores', {
          customSurveyId,
          groupId,
        })
      )
      requests.push(
        this.$store.dispatch('customSurveyResults/loadReports', {
          customSurveyId,
          groupId,
        })
      )
      this.isLoading = true
      try {
        await Promise.all(requests)
      } catch (error) {
        const { status } = error.response
        if (status === 401) {
          this.$refs['modal-unauthorized'].show()
        }
      }
      this.isLoading = false
    },
    switchMetricType(type) {
      this.metricType = type
    },
    getLabelByScore(nps) {
      const labels = this.resources.page.reports.engagement.metric.enps
      const score = nps && nps.score ? nps.score : 0
      switch (true) {
        case score <= 0:
          return labels.low
        case score <= 50:
          return labels.okay
        case score <= 75:
          return labels.good
        case score <= 100:
          return labels.veryGood
        default:
          return labels.low
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

.custom-surveys-reports-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  width: 100%;

  .body {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    overflow: hidden;

    .content {
      padding: 0;
      position: relative;
      z-index: 0;
      min-width: 0px;
      flex: 1 1 auto;
      overflow: auto;
      flex-grow: 1;
      --webkit-flex-grow: 1;

      .loading {
        margin: 0 auto;
        text-align: center;
      }

      header {
        .d-flex {
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }
      }

      .over-score {
        @media (max-width: 767px) {
          flex-direction: column;
        }
      }

      button {
        .material-icons {
          vertical-align: baseline;
        }
        span {
          vertical-align: text-bottom;
        }
      }

      .metrics-container .subTitle {
        color: map-get($theme-colors, 'gray02');
        font-size: 12px;
        text-decoration: none;
        margin: 0;
      }

      .empty-state-box {
        align-items: center;
        padding: 15px;
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
        text-align: center;
        width: 100%;

        .alert {
          z-index: -1;
        }
      }

      .page-reports {
        margin-bottom: 40px;
      }

      .nav.header .nav-link {
        margin: 0rem !important;
      }

      .chart-layout {
        .card-body {
          padding: 0px;
          margin: 0px;
        }
      }

      .card {
        box-shadow: none;
        border-radius: 10px !important;

        .card-header {
          border-top-left-radius: 10px !important;
          border-top-right-radius: 10px !important;
          font-size: 24px;
        }

        .card-body {
          @media (max-width: 1300px) {
            margin: 0px;
            padding: 0px 0px 10px 0px;
          }
          @media (max-width: 990px) {
            padding: 1.25rem;
          }
          @media (max-width: 576px) {
            padding: 0px 0px 10px 0px;
          }
        }

        .flex {
          display: flex;
          justify-content: space-around;
          align-items: center;
        }
      }

      .switch-metric-type {
        font-size: 13px;
      }

      .nps-component {
        display: flex;
        flex-direction: row;
        align-items: flex-start;

        @media (max-width: 767px) {
          flex-direction: column;
        }

        .history {
          flex-grow: 1;
          padding: 0 20px;

          canvas {
            max-height: 300px !important;
          }
        }
      }

      .recommendation-container > .card-body {
        padding: 20px;
      }

      .report-date {
        flex-direction: column;
      }

      .inactive-group-card {
        background: map-get($theme-colors, 'gray08') !important;
        .card-header {
          background: map-get($theme-colors, 'gray08') !important;
        }
      }

      .engagement-notice {
        padding: 15px;
        max-width: 315px;
        text-align: center;
        font-style: italic;
      }

      .enps-empty {
        color: orange;
      }
    }
  }
}
</style>
